/**
 * Template serialization/deserialization
 * Templates save only indicators and chart type from selected chart
 */

import { serializeIndicators, ensureValidIndicators } from "./serialization.js";
import { WORKSPACE_CONFIG } from "../constants.js";

/**
 * Serialize a chart as a template
 * @param {Object} chart - Chart object from store
 * @returns {Object} - Serialized template
 */
export const serializeTemplate = (chart) => {
  if (!chart) {
    throw new Error("Chart is required for template serialization");
  }

  return {
    version: WORKSPACE_CONFIG.CURRENT_VERSION,
    timestamp: new Date().toISOString(),
    chartType: chart.chartType || "candle",
    indicators: serializeIndicators(chart.indicators || []),
    metadata: {
      app: "Odin Sight",
      description: "Chart template with indicators and chart type",
      sourceSymbol: chart.symbol, // For reference only, not used on load
      sourcePeriodicity: chart.periodicity, // For reference only, not used on load
    },
  };
};

/**
 * Deserialize a template and apply to a chart
 * @param {Object} templateData - Serialized template data
 * @param {Object} chartStore - Chart store instance
 * @param {string} chartId - Target chart ID to apply template to
 * @returns {boolean} - Success status
 */
export const deserializeTemplate = (templateData, chartStore, chartId) => {
  try {
    if (!templateData) {
      throw new Error("Template data is required");
    }

    if (!chartStore) {
      throw new Error("Chart store is required");
    }

    if (!chartId) {
      throw new Error("Chart ID is required");
    }

    // Get the target chart
    const chart = chartStore.charts.find((c) => c.id === chartId);
    if (!chart) {
      throw new Error("Chart not found");
    }

    // Deserialize indicators
    const indicators = ensureValidIndicators(templateData.indicators || []);

    // Apply template to chart
    // 1. Clear existing indicators
    const currentIndicators = chart.indicators || [];
    currentIndicators.forEach((indicator) => {
      chartStore.removeIndicatorFromChart(chartId, indicator.id);
    });

    // 2. Set chart type
    if (templateData.chartType) {
      chartStore.updateChartConfig(chartId, {
        chartType: templateData.chartType,
      });
    }

    // 3. Add indicators from template
    // Pass the full enriched indicator, not just partial data
    indicators.forEach((indicator) => {
      // Programmatic apply — don't count each restored indicator as engagement.
      chartStore.addIndicatorToChart(chartId, indicator, {
        skipEngagement: true,
      });
    });
    return true;
  } catch (error) {
    console.error("Failed to deserialize template:", error);
    throw error;
  }
};
